import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Trash2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = ["new", "contacted", "qualified"];
const statusColor = {
  new: "text-oed-gold border-oed-gold/40",
  contacted: "text-blue-300 border-blue-400/40",
  qualified: "text-green-300 border-green-400/40",
};

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, qualified: 0 });
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s, t] = await Promise.all([api.get("/leads"), api.get("/leads/stats"), api.get("/territories")]);
      setLeads(l.data);
      setStats(s.data);
      setTerritories(t.data);
    } catch (err) {
      if (err.response?.status === 401) { nav("/admin/login"); return; }
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("oed_token")) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const logout = () => { localStorage.removeItem("oed_token"); nav("/admin/login"); };

  const setStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    load();
  };

  const remove = async (id) => {
    await api.delete(`/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    setSelected(null);
    toast.success("Lead deleted");
    load();
  };

  const setTerritoryStatus = async (code, status) => {
    setTerritories((ts) => ts.map((t) => (t.code === code ? { ...t, status } : t)));
    try {
      await api.patch(`/territories/${code}`, { status });
      toast.success(`${code} set to ${status}`);
    } catch (e) {
      toast.error("Update failed");
      load();
    }
  };

  const TERR_STATUSES = ["available", "pending", "claimed"];
  const terrColor = {
    available: "text-oed-gold border-oed-gold/40",
    pending: "text-blue-300 border-blue-400/40",
    claimed: "text-red-300 border-red-400/40",
  };

  const cards = [
    { k: "total", label: "Total Applications" },
    { k: "new", label: "New" },
    { k: "contacted", label: "Contacted" },
    { k: "qualified", label: "Qualified" },
  ];

  return (
    <div className="min-h-screen bg-oed-base">
      <header className="border-b border-white/10 bg-oed-surface/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display font-black tracking-tighter">OED</span>
            <span className="h-4 w-px bg-white/20" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">Dealer Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load} className="p-2 text-white/50 hover:text-white transition-colors"><RefreshCw size={16} /></button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Applications</h1>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {cards.map((c) => (
            <div key={c.k} data-testid={`stat-${c.k}`} className="bg-oed-surface p-6">
              <p className="font-display font-black text-4xl tracking-tighter">{stats[c.k]}</p>
              <p className="mt-2 text-xs uppercase tracking-[0.15em] text-white/40">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 border border-white/10">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-white/40">
            <span className="col-span-3">Name</span>
            <span className="col-span-3">Contact</span>
            <span className="col-span-2">Level</span>
            <span className="col-span-2">Status</span>
            <span className="col-span-2 text-right">Date</span>
          </div>

          {loading ? (
            <div className="p-12 text-center text-white/40">Loading…</div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center text-white/40" data-testid="no-leads">No applications yet.</div>
          ) : (
            leads.map((l) => (
              <motion.button
                key={l.id}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                onClick={() => setSelected(l)}
                data-testid={`lead-row-${l.id}`}
                className="w-full grid grid-cols-2 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 border-b border-white/5 text-left hover:bg-white/[0.03] transition-colors"
              >
                <span className="col-span-2 md:col-span-3 font-medium">{l.full_name}</span>
                <span className="col-span-2 md:col-span-3 text-sm text-white/50 truncate">{l.email}<br className="md:hidden" /><span className="hidden md:inline"> · </span>{l.phone}</span>
                <span className="col-span-1 md:col-span-2 text-sm text-white/50 truncate">{l.level}</span>
                <span className="col-span-1 md:col-span-2">
                  <span className={`inline-block text-[10px] uppercase tracking-wider border px-2.5 py-1 ${statusColor[l.status] || "text-white/50 border-white/20"}`}>{l.status}</span>
                </span>
                <span className="col-span-2 md:col-span-2 text-sm text-white/30 md:text-right">{new Date(l.created_at).toLocaleDateString()}</span>
              </motion.button>
            ))
          )}
        </div>

        <div className="mt-16 flex items-baseline justify-between">
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">Territories</h2>
          <span className="text-xs text-white/40">
            {territories.filter((t) => t.status === "available").length} available · {territories.filter((t) => t.status === "claimed").length} claimed
          </span>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {territories.map((t) => (
            <div key={t.code} data-testid={`admin-territory-${t.code}`} className="bg-oed-surface p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium truncate">{t.state}</p>
                <span className={`inline-block mt-1 text-[10px] uppercase tracking-wider border px-2 py-0.5 ${terrColor[t.status]}`}>{t.status}</span>
              </div>
              <div className="flex gap-1.5 shrink-0">
                {TERR_STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`admin-terr-${t.code}-${s}`}
                    onClick={() => setTerritoryStatus(t.code, s)}
                    title={s}
                    className={`w-7 h-7 text-[10px] uppercase border transition-colors ${t.status === s ? "bg-oed-gold text-black border-oed-gold" : "border-white/15 text-white/40 hover:border-white/40"}`}
                  >
                    {s[0]}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl" onClick={() => setSelected(null)} data-testid="lead-detail-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-oed-surface border border-white/10 p-8 md:p-10 max-h-[90vh] overflow-y-auto"
          >
            <p className="text-[11px] uppercase tracking-[0.3em] text-oed-gold">{selected.level}</p>
            <h2 className="mt-3 font-display font-bold text-3xl tracking-tight">{selected.full_name}</h2>
            <div className="mt-6 space-y-3 text-sm">
              <Row label="Email" value={selected.email} />
              <Row label="Phone" value={selected.phone} />
              <Row label="Location" value={[selected.city, selected.state].filter(Boolean).join(", ") || "—"} />
              <Row label="Investment" value={selected.investment_range || "—"} />
              <Row label="Timeline" value={selected.timeline || "—"} />
            </div>
            {selected.message && (
              <div className="mt-6 border-t border-white/10 pt-4">
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-2">Message</p>
                <p className="text-sm text-white/70 leading-relaxed">{selected.message}</p>
              </div>
            )}
            <div className="mt-8 border-t border-white/10 pt-6">
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-3">Update Status</p>
              <div className="flex gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`status-${s}`}
                    onClick={() => setStatus(selected.id, s)}
                    className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${selected.status === s ? "bg-oed-gold text-black border-oed-gold" : "border-white/20 text-white/60 hover:border-white/40"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-8 flex items-center justify-between">
              <button data-testid="lead-delete" onClick={() => remove(selected.id)} className="inline-flex items-center gap-2 text-sm text-red-400/70 hover:text-red-400 transition-colors">
                <Trash2 size={15} /> Delete
              </button>
              <button data-testid="lead-close" onClick={() => setSelected(null)} className="bg-white text-black px-6 py-2.5 text-sm font-medium hover:bg-oed-gold transition-colors">Close</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-white/5 pb-3">
    <span className="text-white/40">{label}</span>
    <span className="text-white/80 text-right">{value}</span>
  </div>
);
